import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';
import { getPlatformDomainName } from '../../config/platformConfig';
import { getStorefrontVariantIdentity } from '../../utils/storefrontVariantUtils';

// Get store slug from URL (works with both path-based and subdomain routing)
const getStoreSlug = () => {
  // First, try to extract from path-based routing (/s/slug)
  const path = window.location.pathname;
  const pathMatch = path.match(/\/s\/([^/]+)/);
  if (pathMatch) {
    return pathMatch[1];
  }

  // Then, try to extract from subdomain (slug.localhost:3000 or slug.domain.com)
  const hostname = window.location.hostname;
  const platformDomain = getPlatformDomainName();
  const platformDomainWithoutPort = platformDomain.split(':')[0];
  const reservedSubdomains = ['admin', 'api', 'www', 'app', 'dashboard', 'console', 'my', 'cdn', 'static'];

  // Remove www. if present
  const cleanHostname = hostname.replace(/^www\./, '');

  // Check if this is a subdomain (not the base domain)
  if (cleanHostname.includes('.') && cleanHostname.endsWith(platformDomainWithoutPort)) {
    const subdomain = cleanHostname.substring(0, cleanHostname.length - platformDomainWithoutPort.length - 1);
    const firstSubdomainPart = subdomain.split('.')[0];

    if (subdomain && !reservedSubdomains.includes(firstSubdomainPart)) {
      return subdomain;
    }
  }

  return '';
};

/**
 * Get store slug with fallback to Redux state (for custom domains)
 * Custom domains dont have slug in URL, so we get it from loaded store data
 */
const getStoreSlugWithFallback = (getState) => {
  // First try URL-based detection
  const urlSlug = getStoreSlug();
  if (urlSlug) {
    return urlSlug;
  }

  // Fallback to Redux state (for custom domains)
  const state = getState();
  const store = state.store?.current;
  if (store?.slug) {
    return store.slug;
  }

  return "";
};

// NOTE: We no longer use localStorage for session IDs because localStorage is NOT shared across subdomains.
// Instead, we rely on backend session cookies (configured in server-postgres-simple.js) which work across subdomains.
// The backend will automatically create and manage sessions via cookies.

// Async thunks for cart operations
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (customerId, { getState }) => {
    const storeSlug = getStoreSlugWithFallback(getState);

    try {
      const response = await axios.get(`/cart/${storeSlug}`, {
        withCredentials: true
      });

      return { items: convertBackendCart(response.data.cart), isGuest: true };
    } catch (error) {
      console.error('Error fetching cart:', error.message);
      return { items: {} };
    }
  }
);

// Generate a composite key for cart items (supports variants)
const getCartItemKey = (productId, variations, variantId) => {
  if (variantId) {
    return `${productId}_${variantId}`;
  }
  if (variations && Object.keys(variations).length > 0) {
    return `${productId}_${JSON.stringify(variations)}`;
  }
  return productId;
};

// Convert backend cart response to frontend format (with variation support)
const convertBackendCart = (cartData) => {
  const items = {};
  if (cartData?.items) {
      cartData.items.forEach(item => {
        const productObj = item.product || item.productId;
        const productId = productObj?._id || productObj?.id;
        if (productObj && productId) {
          const variantIdentity = getStorefrontVariantIdentity({
            id: item.variantId || item.variant?.id || productObj.variantId || null,
            sku: item.variantSku || productObj.variantSku || null,
            combination: item.variantCombination || item.variations || null,
            name: item.variantName || productObj.variantName || null
          });
          const productWithCorrectPrice = {
            ...productObj,
            price: item.price !== undefined ? item.price : productObj.price,
            variantId: item.variantId || productObj.variantId || null,
            variantSku: item.variantSku || productObj.variantSku || null,
            variantName: item.variantName || productObj.variantName || null,
            variantCombination: item.variantCombination || item.variations || null
          };
          const key = getCartItemKey(productId, item.variations, item.variantId || productObj.variantId || null);
          items[key] = {
            product: productWithCorrectPrice,
            qty: item.quantity,
            variantId: variantIdentity.variantId || item.variantId || productObj.variantId || null
          };
          if (item.variations) items[key].variations = item.variations;
          if (item.variantSku) items[key].variantSku = item.variantSku;
          if (item.variantName) items[key].variantName = item.variantName;
          if (item.variantCombination) items[key].variantCombination = item.variantCombination;
          // Unit-based selling fields
          if (item.sellByUnit) items[key].sellByUnit = item.sellByUnit;
          if (item.unitOfMeasure) items[key].unitOfMeasure = item.unitOfMeasure;
        if (item.unitQuantity) items[key].unitQuantity = item.unitQuantity;
        if (item.pricePerUnit) items[key].pricePerUnit = item.pricePerUnit;
      }
    });
  }
  return items;
};

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async ({ product, quantity = 1, variations, variantId, variantSku, variantName, variantCombination }, { getState }) => {
    const storeSlug = getStoreSlugWithFallback(getState);

    try {
      const payload = {
        productId: product.id || product._id,
        quantity,
        price: product.price
      };
      if (variations) payload.variations = variations;
      if (product.variations) payload.variations = product.variations;
      if (variantId) payload.variantId = variantId;
      if (product.variantId) payload.variantId = product.variantId;
      if (variantSku) payload.variantSku = variantSku;
      if (product.variantSku) payload.variantSku = product.variantSku;
      if (variantName) payload.variantName = variantName;
      if (product.variantName) payload.variantName = product.variantName;
      if (variantCombination) payload.variantCombination = variantCombination;
      if (product.variantCombination) payload.variantCombination = product.variantCombination;
      // Unit-based selling fields
      if (product.sellByUnit) payload.sellByUnit = product.sellByUnit;
      if (product.unitOfMeasure) payload.unitOfMeasure = product.unitOfMeasure;
      if (product.unitQuantity) payload.unitQuantity = product.unitQuantity;
      if (product.pricePerUnit) payload.pricePerUnit = product.pricePerUnit;

      const response = await axios.post(`/cart/${storeSlug}/add`, payload, {
        withCredentials: true
      });

      return { items: convertBackendCart(response.data.cart) };
    } catch (error) {
      // Keep the server response in development logs. This is essential when
      // a storefront cart is rejected because a product or its stock changed.
      console.error('Error adding to cart:', error.response?.data || error.message);
      throw error;
    }
  }
);

export const updateQuantity = createAsyncThunk(
  'cart/updateQuantity',
  async ({ productId, quantity, variations, variantId }, { getState }) => {
    const storeSlug = getStoreSlugWithFallback(getState);

    try {
      const payload = { productId, quantity };
      if (variations) payload.variations = variations;
      if (variantId) payload.variantId = variantId;

      const response = await axios.put(`/cart/${storeSlug}/update`, payload, {
        withCredentials: true
      });

      return { items: convertBackendCart(response.data.cart) };
    } catch (error) {
      console.error('Error updating cart in backend:', error);
      throw error;
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async ({ productId, variations, variantId }, { getState }) => {
    const storeSlug = getStoreSlugWithFallback(getState);

    try {
      const data = { productId };
      if (variations) data.variations = variations;
      if (variantId) data.variantId = variantId;

      const response = await axios.delete(`/cart/${storeSlug}/remove`, {
        data,
        withCredentials: true
      });

      return { items: convertBackendCart(response.data.cart) };
    } catch (error) {
      console.error('Error removing from cart in backend:', error);
      throw error;
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { getState }) => {
    const storeSlug = getStoreSlugWithFallback(getState);

    try {
      await axios.delete(`/cart/${storeSlug}/clear`, {
        withCredentials: true
      });

      return { items: {} };
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }
);

export const mergeCart = createAsyncThunk(
  'cart/mergeCart',
  async (_, { getState }) => {
    const storeSlug = getStoreSlugWithFallback(getState);

    try {
      const response = await axios.get(`/cart/${storeSlug}`, {
        withCredentials: true
      });

      return { items: convertBackendCart(response.data.cart) };
    } catch (error) {
      console.error('Error merging cart:', error.message);
      throw error;
    }
  }
);

const initialState = {
  items: {},
  loading: false,
  error: null
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // Clear any errors
    clearError: (state) => {
      state.error = null;
    },
    // Reset cart state
    resetCart: (state) => {
      state.items = {};
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch cart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Add to cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update quantity
      .addCase(updateQuantity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuantity.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
      })
      .addCase(updateQuantity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Remove from cart
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Clear cart
      .addCase(clearCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Merge cart
      .addCase(mergeCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mergeCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
      })
      .addCase(mergeCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const selectCartCount = createSelector(
  [(state) => state.cart.items],
  (items) => Object.values(items).reduce((acc, it) => acc + it.qty, 0)
);

export const selectCartItems = createSelector(
  [(state) => state.cart.items],
  (items) => Object.values(items)
);

export const selectCartTotal = createSelector(
  [(state) => state.cart.items],
  (items) => Object.values(items).reduce((acc, it) => acc + it.product.price * it.qty, 0)
);

export const { clearError, resetCart } = cartSlice.actions;
export default cartSlice.reducer;
